package com.exura.pages.register

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.exura.features.localization.commonLocalization
import com.exura.features.newauth.domain.manager.AuthManager
import com.exura.features.newauth.domain.model.ProviderUserRole
import com.exura.features.newauth.usecase.RandomRegisterProviderUseCase
import com.exura.features.userprofile.domain.model.DoctorProfile
import com.exura.features.userprofile.domain.model.OrganizationProfile
import com.exura.features.userprofile.domain.usecase.SaveProviderProfileUseCase
import com.exura.navigation.Route
import com.exura.util.StringValidator
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.core.PageContext

sealed interface RegisterEvent {
    data class EmailChanged(val email: String) : RegisterEvent
    data class RoleSelected(val role: ProviderUserRole) : RegisterEvent
    data class FullNameChanged(val fullName: String) : RegisterEvent
    data class SpecializationChanged(val specialization: String) : RegisterEvent
    data class OrganizationNameChanged(val name: String) : RegisterEvent
    data class FieldChanged(val field: String) : RegisterEvent
    data class ContactNumberChanged(val number: String) : RegisterEvent
    data object RegisterClicked : RegisterEvent
    data object NavigateToLogin : RegisterEvent
}

data class RegisterUiState(
    val email: String = "",
    val emailError: String = "",
    val selectedRole: ProviderUserRole = ProviderUserRole.DOCTOR,
    val fullName: String = "",
    val specialization: String = "",
    val organizationName: String = "",
    val field: String = "",
    val contactNumber: String = "",
    val isRegistering: Boolean = false,
    val registrationError: String? = null,
    val showSuccessMessage: Boolean = false,
)

class RegisterController(
    private val context: PageContext,
    private val authManager: AuthManager,
    private val randomRegisterProviderUseCase: RandomRegisterProviderUseCase,
    private val saveProviderProfileUseCase: SaveProviderProfileUseCase,
) {
    var uiState by mutableStateOf(RegisterUiState())
        private set

    suspend fun handleEvent(event: RegisterEvent) {
        when (event) {
            is RegisterEvent.EmailChanged -> onEmailChanged(event.email)
            is RegisterEvent.RoleSelected -> onRoleSelected(event.role)
            is RegisterEvent.FullNameChanged -> onFullNameChanged(event.fullName)
            is RegisterEvent.SpecializationChanged -> onSpecializationChanged(event.specialization)
            is RegisterEvent.OrganizationNameChanged -> onOrganizationNameChanged(event.name)
            is RegisterEvent.FieldChanged -> onFieldChanged(event.field)
            is RegisterEvent.ContactNumberChanged -> onContactNumberChanged(event.number)
            is RegisterEvent.RegisterClicked -> onRegisterClicked()
            is RegisterEvent.NavigateToLogin -> onNavigateToLogin()
        }
    }

    private fun onEmailChanged(email: String) {
        uiState = uiState.copy(
            email = email,
            emailError = ""
        )
    }

    private fun onRoleSelected(role: ProviderUserRole) {
        uiState = uiState.copy(selectedRole = role)
    }

    private fun onFullNameChanged(fullName: String) {
        uiState = uiState.copy(fullName = fullName)
    }

    private fun onSpecializationChanged(specialization: String) {
        uiState = uiState.copy(specialization = specialization)
    }

    private fun onOrganizationNameChanged(name: String) {
        uiState = uiState.copy(organizationName = name)
    }

    private fun onFieldChanged(field: String) {
        uiState = uiState.copy(field = field)
    }

    private fun onContactNumberChanged(number: String) {
        uiState = uiState.copy(contactNumber = number)
    }

    private suspend fun onRegisterClicked() {
        val emailError = StringValidator.getEmailError(uiState.email)
        uiState = uiState.copy(emailError = emailError)

        if (emailError.isBlank()) {
            uiState = uiState.copy(
                isRegistering = true,
                registrationError = null
            )

            val profile = if (uiState.selectedRole == ProviderUserRole.DOCTOR) {
                DoctorProfile(
                    email = uiState.email,
                    fullName = uiState.fullName.takeIf { it.isNotBlank() },
                    specialization = uiState.specialization.takeIf { it.isNotBlank() },
                )
            } else {
                OrganizationProfile(
                    email = uiState.email,
                    name = uiState.organizationName.takeIf { it.isNotBlank() },
                    field = uiState.field.takeIf { it.isNotBlank() },
                    patientsContactNumber = uiState.contactNumber.takeIf { it.isNotBlank() },
                )
            }

            randomRegisterProviderUseCase(uiState.email, uiState.selectedRole)
                .onSuccess {
                    saveProviderProfileUseCase(profile).getOrThrow()
                    uiState = uiState.copy(
                        isRegistering = false,
                        showSuccessMessage = true
                    )
                    authManager.signOut()
                }
                .onFailure { error ->
                    uiState = uiState.copy(
                        isRegistering = false,
                        registrationError = error.message ?: registerLocalization.current.errors.registrationFailed
                    )
                }
        }
    }

    private fun onNavigateToLogin() {
        context.router.navigateTo(Route.LOGIN)
    }
}

@Composable
fun rememberRegisterController(
    context: PageContext,
): RegisterController {
    val randomRegisterProviderUseCase = injectAndRemember<RandomRegisterProviderUseCase>()
    val saveProviderProfileUseCase = injectAndRemember<SaveProviderProfileUseCase>()
    val authManager = injectAndRemember<AuthManager>()
    return remember(context, authManager, randomRegisterProviderUseCase, saveProviderProfileUseCase) {
        RegisterController(
            context = context,
            authManager = authManager,
            randomRegisterProviderUseCase = randomRegisterProviderUseCase,
            saveProviderProfileUseCase = saveProviderProfileUseCase,
        )
    }
}
